import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, Wallet, CheckCircle2 } from 'lucide-react';
import { formatCurrency, formatDate, formatLongDate, formatPeriod, isBlankDate } from '../utils/format';

interface Overview {
  nextSettlement?: {
    periodStart: string;
    periodEnd: string;
    orders: number;
    freightAmount: number;
    commissionAmount: number;
    fixedCostAmount: number;
    expenses: number;
    expenseAmount: number;
    estimatedNetAmount: number;
  };
  openInvoices?: { count: number; remainingAmount: number; nextDueDate: string };
  lastPayment?: { documentNo: string; paidDate: string; amountIncludingVAT: number; settlementPeriodStart: string; settlementPeriodEnd: string };
}

/**
 * The three things a vendor wants first: what the next settlement will roughly be, what is on its
 * way to them (posted, not yet paid), and what they were last paid. Figures from BC (PortalOverview).
 */
export default function PayoutOverview({ overview }: { overview?: Overview }) {
  const next = overview?.nextSettlement;
  if (!next) return null;
  const open = overview?.openInvoices;
  const last = overview?.lastPayment;

  return (
    <section aria-label="Utbetalningar" className="mb-8 grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
      {/* Next settlement estimate */}
      <div className="card card-accent p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-brass">
              <CalendarClock className="h-4 w-4" aria-hidden />
              Nästa avräkning
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {formatLongDate(next.periodEnd)} · transaktioner t.o.m. detta datum
            </p>
          </div>
          <Link
            to="/settlement-orders"
            className="group inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm text-blue-700 ring-1 ring-inset ring-blue-200 transition-all duration-300 ease-fluid hover:bg-blue-50"
          >
            Transaktioner
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 ease-fluid group-hover:-translate-y-[1px] group-hover:translate-x-0.5" aria-hidden />
          </Link>
        </div>

        <p className="mt-6 text-sm text-gray-600">Beräknat netto exkl. moms</p>
        <p className={`text-4xl font-semibold tracking-tight tabular-nums ${next.estimatedNetAmount < 0 ? 'text-red-700' : 'text-ink'}`}>
          {formatCurrency(next.estimatedNetAmount, 0)}
        </p>

        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 pt-5 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-gray-500">Frakt ({next.orders} st)</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink">{formatCurrency(next.freightAmount, 0)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Provision</dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink">−{formatCurrency(next.commissionAmount, 0)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">
              <Link to="/fixed-costs" className="hover:text-blue-700">Fasta kostnader</Link>
            </dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink">−{formatCurrency(next.fixedCostAmount, 0)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">
              <Link to="/open-expenses" className="hover:text-blue-700">Utgifter ({next.expenses} st)</Link>
            </dt>
            <dd className="mt-0.5 font-medium tabular-nums text-ink">−{formatCurrency(next.expenseAmount, 0)}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-gray-500">
          Uppskattning utifrån det som är godkänt i dag. Vilande transaktioner ingår inte; slutligt belopp står på avräkningen.
        </p>
      </div>

      {/* On its way / last paid */}
      <div className="grid gap-6">
        <Link to="/invoices" className="card card-hover block p-6">
          <p className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <Wallet className="h-4 w-4 text-blue-600" aria-hidden />
            På väg till dig
          </p>
          <p className="mt-2 text-2xl font-semibold tabular-nums text-ink">{formatCurrency(open?.remainingAmount, 0)}</p>
          <p className="mt-1 text-xs text-gray-500">
            {open && open.count > 0
              ? `${open.count} ${open.count === 1 ? 'obetald avräkning' : 'obetalda avräkningar'}${isBlankDate(open.nextDueDate) ? '' : ` · nästa förfaller ${formatDate(open.nextDueDate)}`}`
              : 'Alla avräkningar är betalda'}
          </p>
        </Link>

        <div className="card p-6">
          <p className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
            Senaste utbetalning
          </p>
          {last ? (
            <>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-ink">{formatCurrency(last.amountIncludingVAT, 0)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {formatDate(last.paidDate)} ·{' '}
                <Link to={`/invoices/invoice/${encodeURIComponent(last.documentNo)}`} className="font-mono text-blue-700 hover:underline">
                  {last.documentNo}
                </Link>{' '}
                · {formatPeriod(last.settlementPeriodStart, last.settlementPeriodEnd)}
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-gray-500">Ingen utbetalning registrerad ännu.</p>
          )}
        </div>
      </div>
    </section>
  );
}
